import {useEffect, useMemo, useRef, useState} from 'react';

import {getAppLanguage, getConnectedUsersWithAndWithoutBookings, UICalendarDay, UICalendarWeek} from '@lib/store';

import {Loader} from '@atoms';
import {useAppSelector, useModal} from '@hooks';
import {trackPageComponentedRendered} from '@lib/infrastructure';
import {constructCalendarOverviewConnectionFilter, useFilterData} from '@lib/logic';
import {formatDate} from '@lib/utils';
import {IconButton} from '@molecules';
import {
  CalendarHorizontalWeekOverview,
  CalendarOverviewConnectionFilter,
  CalendarWeek,
  NewFilterCard,
} from '@organisms';
import {FlexRow, H1, H4} from '@quarks';
import {
  CalendarLayout,
  CalendarWeekOverview,
  Canvas,
  Heading,
  CalendarDayOverview as StyledCalendarDayOverview,
} from '@templates';
import {addDays, addWeeks, eachDayOfInterval, format, getISOWeek, isSameDay, parseISO, startOfISOWeek} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {Navigate, useParams} from 'react-router-dom';
import {CalendarWeekDay_timeslots} from 'src/components/organisms/calendar/CalendarWeekDay/CalendarWeekDay_timeslots';
import {CalendarHorizontalWeekOverviewWrapper, CalendarPageHeader, OverviewWrapper} from './styles';
import {CalendarDayOverview_timeslots} from 'src/components/organisms/calendar/CalendarDayOverview/CalendarDayOverview_timeslots';

export const Calendar_timeslots = () => {
  const appLanguage = useAppSelector(getAppLanguage);

  const {addPages, openModal} = useModal();

  const [showCalendar, setShowCalendar] = useState(true);

  // Note: for long sessions we should update this if we don't want to rely on there being a re-render
  //       every now and then
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const calendarWeeks = useMemo<UICalendarWeek[]>(() => {
    const WEEKS_VISIBLE = 4;
    const today = new Date(todayKey);

    const refDates = eachDayOfInterval(
      {
        start: today,
        end: addWeeks(today, WEEKS_VISIBLE - 1),
      },
      {step: 7},
    );

    return refDates.map((d, i): UICalendarWeek => {
      const weekNumber = getISOWeek(d);
      const weekStart = startOfISOWeek(d);

      return {
        weekNumber,
        weekType: i === 0 ? 'thisWeek' : i === 1 ? 'nextWeek' : 'futureWeek',
        data: eachDayOfInterval({
          start: weekStart,
          end: addDays(weekStart, 6),
        }).map((wd): UICalendarDay => {
          return {
            date: format(wd, 'yyyy-MM-dd'),
            isToday: isSameDay(today, wd),
            weekNumber,
          };
        }),
      };
    });
  }, [todayKey]);

  // TODO: move to a selector?
  const isLoading = useAppSelector((state) => {
    for (const week of calendarWeeks) {
      for (const day of week.data) {
        const workdays = state.data.timeslots.workdaysByDate[day.date];
        const reservations = state.data.timeslots.workspaceReservationsByDate[day.date];

        if (!workdays || !reservations) {
          console.warn('Warning: no data load for value that is displayed in calendar');
        }
        if (!workdays || !reservations || workdays.status === 'pending' || reservations.status === 'pending') {
          return true;
        }
      }
    }
  });

  const {t} = useTranslation();

  const toggleDayAndWeekView = () => {
    window.scrollTo(0, 0);
    setShowCalendar((prev) => !prev);
  };

  const {connectionsWithWorkdays, connectionsWithoutWorkdays} = useAppSelector(getConnectedUsersWithAndWithoutBookings);
  const filters = useMemo(
    () => constructCalendarOverviewConnectionFilter(connectionsWithWorkdays, connectionsWithoutWorkdays, t),
    [connectionsWithWorkdays, connectionsWithoutWorkdays, t],
  );
  const {filteredData} = useFilterData(
    'CalendarOverview_Connections',
    'cacheKey',
    [...connectionsWithWorkdays, ...connectionsWithoutWorkdays],
    filters,
    'or',
    isLoading,
  );
  const selectedCalendarConnectionFilterIds = filteredData.map((c) => c.id);

  useEffect(() => {
    trackPageComponentedRendered('calendar', !isLoading);
  }, [isLoading]);

  // Get the active date from the path
  const {date: selectedDateKey} = useParams();

  // Redirect all invalid things from the path to today (note: no 404)
  const selectedDateIsInRange = calendarWeeks.some((w) => w.data.some((d) => d.date === selectedDateKey));
  const redirectToToday = !selectedDateKey || !selectedDateIsInRange;

  const scrolledOnFirstRender = useRef(false);
  useEffect(
    function scrollToSelectedDate() {
      if (redirectToToday || scrolledOnFirstRender.current) return;

      // TODO: this should be triggered by the actual day tile that renders with the isToday state.
      //       the current timeout will not be a reliable guarantee that the element exists and is
      //       ready to be scrolled to. Also, it targets body rather than its own subtree...
      let to = setTimeout(() => {
        const scrollElement = document.querySelector(`[data-scroll-to="${selectedDateKey}"]`);
        scrollElement?.scrollIntoView({behavior: 'smooth', block: 'start'});

        scrolledOnFirstRender.current = true;
      }, 800);

      return () => {
        clearTimeout(to);
      };
    },
    [selectedDateKey],
  );

  if (redirectToToday) return <Navigate to={`/calendar/${todayKey}`} />;

  const selectedLabel = formatDate(parseISO(selectedDateKey), 'EEEE d MMMM', appLanguage);

  if (isLoading) {
    return (
      <>
        <Heading>
          <CalendarPageHeader title={showCalendar ? t('translation:Calendar') : selectedLabel} />
        </Heading>
        <Canvas centerContent>
          <Loader />
        </Canvas>
      </>
    );
  }

  function openFilterModal() {
    addPages([
      <NewFilterCard
        filterSlice={'CalendarOverview_Connections'}
        searchBarPlaceholderText={t('connection:SearchConnectionPlaceholder')}
        searchByProperties={['email', 'name']}
      />,
    ]);
    openModal();
  }

  return (
    <>
      <Heading>
        {showCalendar ? (
          <CalendarPageHeader className="gap-4 justify-between items-center flex">
            <H1>{t('translation:Calendar')}</H1>
            <IconButton
              icon="filter"
              iconButton="tertiary"
              onClick={openFilterModal}
            />
          </CalendarPageHeader>
        ) : (
          <CalendarPageHeader className="gap-4 justify-center items-center grid grid-cols-[1fr_4fr_1fr]">
            <IconButton
              className=" aligself-start justify-self-start bg-transparent shadow-none"
              icon={'caretLeft'}
              iconButton="tertiary"
              onClick={() => setShowCalendar(true)}
            />
            <H4 className="text-center">{selectedLabel}</H4>
          </CalendarPageHeader>
        )}

        <FlexRow
          alignItems="center"
          justifyContent="center"
          sm={{display: 'none'}}>
          <H4 data-testid="pages-Calendar-Calendar_calendarHeader">ksk</H4>
        </FlexRow>
      </Heading>

      <CalendarLayout>
        <StyledCalendarDayOverview $showCalendar={showCalendar}>
          <CalendarHorizontalWeekOverviewWrapper>
            <CalendarHorizontalWeekOverview calendarWeeks={calendarWeeks} />
          </CalendarHorizontalWeekOverviewWrapper>
          <OverviewWrapper>
            <FlexRow
              alignItems="center"
              justifyContent="center"
              sm={{display: 'none'}}>
              <H1 data-testid="pages-Calendar-Calendar_calendarHeader">{selectedLabel}</H1>
            </FlexRow>
            <CalendarDayOverview_timeslots date={selectedDateKey} />
          </OverviewWrapper>
        </StyledCalendarDayOverview>

        <CalendarWeekOverview $showCalendar={showCalendar}>
          <CalendarOverviewConnectionFilter />

          {calendarWeeks.map((data) => (
            <CalendarWeek
              key={`calendarCard-${data.weekNumber}`}
              weekNumber={data.weekNumber}
              weekType={data.weekType}>
              {data.data.map((day) => (
                <CalendarWeekDay_timeslots
                  date={day.date}
                  key={day.date}
                  isToday={day.isToday}
                  toggleDayAndWeekView={toggleDayAndWeekView}
                  showConnectionsWithIds={selectedCalendarConnectionFilterIds}
                />
              ))}
            </CalendarWeek>
          ))}
        </CalendarWeekOverview>
      </CalendarLayout>
    </>
  );
};
